import _ from 'lodash';
import * as dynamicGridSagaActions from 'models/dynamicGrid/sagaActions';
import * as dynamicGridSelectors from 'models/dynamicGrid/selector';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AgGrid from 'molecules/ReactGrid/agGrid/agGrid';
import { isLoadingEnabled } from 'models/loaders/selectors';
import FullScreen from 'atoms/fullScreen/fullScreen';
import { formatThousands } from 'utils/utilities';
import { TProps } from '.';
import './thetaGrid.scss';

const ThetaGridPaginatedAg = (props: TProps) => {
	const {
		config,
		parent,
		isStorageEnabled = true,
		offset,
		onDataRender,
		hasChildRows,
		config: { groupBy, childCellRendererParams = null },
		refreshTimestamp,
	} = props;
	const dispatch = useDispatch();
	const gridConfig = { ...config.gridConfig };

	const data = useSelector((state) => dynamicGridSelectors.getGridData(state, config.tableId));

	const isLoadingColumnSync = useSelector((state) => isLoadingEnabled(state, 'sync-grid-columns'));

	const dataLoading = useSelector((state) => dynamicGridSelectors.isLoading(state, config.tableId.toString()));

	React.useEffect(() => {
		if (!_.isNull(config.url)) {
			dispatch(
				dynamicGridSagaActions.loadGrid({
					tableId: config.tableId,
					url: config.dataProviderOptions.url,
					// pathParams: config.dataProviderOptions.pathParams,
					// queryParams: config.dataProviderOptions.queryParams,
					// service: config.dataProviderOptions.service,
					selectRowKey: config.selectRowKey,
					childTableSelectRowKey: config.childTableSelectRowKey,
					dataProviderOptions: config.dataProviderOptions,
					isAdvancedSearch: true,
					isReset: false,
					extendedSearchFilters: config.extendedSearchFilters,
					isStorageEnabled,
					isUploadEnabled: config?.gridConfig?.isUploadGrid,
				})
			);
		}
	}, [dispatch, config.tableId, config.url, config.extendedSearchFilters, config.refreshTimestamp, isStorageEnabled]);

	let filters = useSelector((state) => dynamicGridSelectors.getGridFilters(state, config.tableId));

	const updatePerPage = useCallback(
		(itemsPerPage: number) => {
			dispatch(
				dynamicGridSagaActions.updateGrid({
					tableId: config.tableId,
					filters: {
						itemsPerPage,
						pageNumber: 1,
					},
					isView: false,
				})
			);
		},
		[config.tableId, dispatch]
	);
	const updatePageNo = useCallback(() => {
		dispatch(dynamicGridSagaActions.lazyLoad({ tableId: config.tableId }));
	}, [dispatch, config]);

	gridConfig.sorting = filters?.isMultiSort
		? _.map(filters?.multiSortColumns, ({ sortDirection, sortField }) => ({
				order: sortDirection,
				field: sortField,
			}))
		: {
				field: filters?.sortField,
				order: filters?.sortDirection?.toLowerCase(),
			};

	return (
		<Stack isVertical gutter="small">
			<StackItem isGrow isStretch className="ag-grid-container-sku">
				{
					<FullScreen>
						<AgGrid
							data={data}
							onDomRef={(ref: any) => {
								props?.onDomRef && props?.onDomRef(ref);
							}}
							key={config.tableId}
							groupBy={groupBy}
							parent={parent}
							childGridDataProviderOptions={config.childGridDataProviderOptions}
							selectRowKey={config.selectRowKey}
							config={gridConfig}
							tableId={config.tableId}
							changePer={updatePerPage}
							isFilterEnabled={config.isFilterEnabled}
							changePage={updatePageNo}
							currentPage={filters?.pageNumber}
							// handleUpdateSort={handleUpdateSort}
							isStorageEnabled={isStorageEnabled}
							refreshTimestamp={refreshTimestamp}
							perPage={filters?.itemsPerPage}
							isLoading={config.url && dataLoading}
							isServerHandled
							hasChildRows={hasChildRows}
							offset={offset}
							isPaginated
							onDataRender={onDataRender}
							showOpenedGroup={props.config.showOpenedGroup}
							groupHideOpenParents={props.config.groupHideOpenParents}
							childCustomActions={config.childCustomActions}
							childCellRendererParams={childCellRendererParams}
						/>
					</FullScreen>
				}
			</StackItem>
			<StackItem isStretch align="right">
				<Stack gutter="small">
					<StackItem isGrow>{isLoadingColumnSync && 'Saving column changes...'}</StackItem>
					<StackItem>
						<div className="pageInfo m-0">
							{data?.[0]?.TotalRecordCount
								? `Total Record Count: ${formatThousands(data?.[0]?.TotalRecordCount)}`
								: ''}
						</div>
					</StackItem>
				</Stack>
			</StackItem>
		</Stack>
	);
};

export default ThetaGridPaginatedAg;
